import type { Topic } from "@/mocks/home";
import { topics } from "@/mocks/home";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "./SectionHeading";

type TopicWallProps = {
  activeTag: string | null;
  onSelectTag: (label: string) => void;
};

const toneDot: Record<Topic["tone"], string> = {
  primary: "bg-primary-500",
  accent: "bg-accent-500",
  secondary: "bg-secondary-400",
};

export default function TopicWall({ activeTag, onSelectTag }: TopicWallProps) {
  return (
    <section id="topics" className="w-full border-b border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6">
        <SectionHeading
          icon="ri-price-tag-3-line"
          eyebrow="话题墙 · TOPIC WALL"
          title="大家正在追的这些话题，热度都写在了标签上"
          desc="每一条线报都会挂到对应的话题标签下。点一下标签，下方线报流会立刻筛出同话题内容，方便你把来龙去脉串起来看。"
        />

        <Reveal>
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {topics.map((topic) => {
              const selected = activeTag === topic.label;
              const base =
                "group flex min-h-[112px] w-full flex-col justify-between rounded-lg border p-4 text-left transition-all duration-300 cursor-pointer";

              const style = topic.hot
                ? "border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600"
                : selected
                  ? "border-primary-400 bg-primary-100/70 text-foreground-950"
                  : "border-background-300 bg-background-50 text-foreground-950 hover:border-primary-300 hover:bg-background-100";

              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => onSelectTag(topic.label)}
                  className={`${base} ${style} ${topic.hot ? "sm:col-span-2 lg:col-span-2" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-heading text-base font-bold leading-snug md:text-lg">
                      {topic.label}
                    </span>
                    {topic.hot && (
                      <span className="flex shrink-0 items-center gap-1 rounded-full bg-background-50/20 px-2 py-0.5 text-[10px] font-label">
                        <i className="ri-fire-fill text-[11px]" />
                        热
                      </span>
                    )}
                    {!topic.hot && (
                      <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${toneDot[topic.tone]}`} />
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span
                      className={`text-xs font-label ${
                        topic.hot ? "text-background-100" : "text-foreground-600"
                      }`}
                    >
                      {topic.count.toLocaleString("en-US")} 条线报
                    </span>
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full transition-transform duration-300 group-hover:translate-x-0.5 ${
                        topic.hot
                          ? "bg-background-50/20 text-background-50"
                          : "bg-background-200 text-foreground-700 group-hover:bg-primary-100 group-hover:text-primary-700"
                      }`}
                    >
                      <i className="ri-arrow-right-line text-sm" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={80}>
          <p className="mt-6 flex items-center gap-2 text-xs text-foreground-600">
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className="ri-information-line" />
            </span>
            点击任意话题标签，即可跳到下方「最新线报」并按该话题筛选。
          </p>
        </Reveal>
      </div>
    </section>
  );
}